import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Loader2, Search, FileText, DollarSign, ExternalLink, RefreshCw,
  CheckCircle2, Clock, Receipt, AlertTriangle, Mail, ShieldAlert,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * /admin/invoices — read-only financial dashboard backed by the
 * payment_transactions Mongo collection. ADMIN role only — managers
 * and auditors are bounced to the dashboard with a toast.
 *
 * The page intentionally keeps Stripe-specific URLs and amounts in
 * one consolidated surface so finance ops can answer "did Mrs. Smith
 * pay yet?" without bouncing between Stripe Dashboard + the app.
 */
const STATUS_STYLES = {
  initiated:    { label: 'Outstanding', cls: 'bg-amber-100 text-amber-700 border border-amber-200' },
  unpaid:       { label: 'Outstanding', cls: 'bg-amber-100 text-amber-700 border border-amber-200' },
  pending:      { label: 'Pending',     cls: 'bg-blue-100 text-blue-700 border border-blue-200' },
  paid:         { label: 'Paid',        cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  refunded:     { label: 'Refunded',    cls: 'bg-gray-200 text-gray-700' },
};

const AdminInvoices = () => {
  const { getAuthHeader, adminRole } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ invoices: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [resendingId, setResendingId] = useState(null);

  // Hard gate — only role=='admin' may visit this page.
  useEffect(() => {
    if (adminRole && adminRole !== 'admin') {
      toast.error('Invoices are visible to the admin role only.');
      navigate('/admin/dashboard', { replace: true });
    }
  }, [adminRole, navigate]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? '' : `?status_filter=${filter}`;
      const res = await axios.get(`${API}/api/admin/invoices${params}`, getAuthHeader());
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [filter, getAuthHeader]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleResend = async (sessionId, customerEmail) => {
    if (!window.confirm(`Resend the invoice email to ${customerEmail}?`)) return;
    setResendingId(sessionId);
    try {
      await axios.post(`${API}/api/admin/invoices/${sessionId}/resend`, {}, getAuthHeader());
      toast.success(`Invoice email re-sent to ${customerEmail}.`);
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend');
    } finally {
      setResendingId(null);
    }
  };

  const filtered = data.invoices.filter(inv => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${inv.customer_email} ${inv.customer_name} ${inv.coupon_code || ''}`
      .toLowerCase().includes(q);
  });

  // Re-compute summary client-side off the FILTERED view so the user
  // can see "Outstanding: $X" on whichever slice they're looking at.
  const summary = data.summary || { total_count: 0, outstanding_count: 0, paid_count: 0, outstanding_amount: 0, paid_amount: 0 };

  const filters = [
    { key: 'outstanding', label: 'Outstanding', count: summary.outstanding_count },
    { key: 'paid',        label: 'Paid',        count: summary.paid_count },
    { key: 'refunded',    label: 'Refunded',    count: data.invoices.filter(i => i.payment_status === 'refunded').length },
    { key: 'all',         label: 'All',         count: summary.total_count },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="admin-invoices">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Receipt size={24} className="text-violet-600" /> Invoices
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Every Stripe invoice issued to a customer — outstanding, paid, and historical.
          </p>
        </div>
        <Badge className="bg-violet-50 text-violet-700 border border-violet-200 text-xs px-2 py-1">
          <ShieldAlert size={12} className="mr-1" /> Admin only · financial data
        </Badge>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5 border-0 shadow-sm" data-testid="summary-outstanding">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-amber-600 font-semibold">Outstanding</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${summary.outstanding_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{summary.outstanding_count} invoice{summary.outstanding_count === 1 ? '' : 's'}</p>
            </div>
            <Clock className="text-amber-400" size={36} />
          </div>
        </Card>
        <Card className="p-5 border-0 shadow-sm" data-testid="summary-paid">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-emerald-600 font-semibold">Collected</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${summary.paid_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{summary.paid_count} invoice{summary.paid_count === 1 ? '' : 's'}</p>
            </div>
            <CheckCircle2 className="text-emerald-400" size={36} />
          </div>
        </Card>
        <Card className="p-5 border-0 shadow-sm" data-testid="summary-total">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-violet-600 font-semibold">Total issued</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{summary.total_count}</p>
              <p className="text-xs text-gray-500 mt-0.5">all-time invoices</p>
            </div>
            <FileText className="text-violet-400" size={36} />
          </div>
        </Card>
      </div>

      {/* Filter chips + search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex flex-wrap gap-2" data-testid="invoice-filters">
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === f.key
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`invoice-filter-${f.key}`}
            >
              {f.label} {f.count > 0 && `(${f.count})`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by email, name, coupon…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 text-sm h-9 w-72"
              data-testid="invoice-search"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchInvoices}
            className="text-violet-700 border-violet-200 hover:bg-violet-50"
            data-testid="invoice-refresh"
          >
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Invoice rows */}
      {loading ? (
        <div className="flex justify-center p-16"><Loader2 size={28} className="animate-spin text-violet-500" /></div>
      ) : filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map(inv => {
            const sty = STATUS_STYLES[inv.payment_status] || STATUS_STYLES.initiated;
            const created = inv.created_at ? new Date(inv.created_at) : null;
            const paid = inv.post_payment_handled_at ? new Date(inv.post_payment_handled_at) : null;
            const isPaid = inv.payment_status === 'paid';
            return (
              <Card
                key={inv.session_id}
                className="p-4 border-0 shadow-sm hover:shadow-md transition-all"
                data-testid={`invoice-row-${inv.session_id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900 text-sm">
                        {inv.customer_name || inv.customer_email}
                      </p>
                      <Badge className={`${sty.cls} text-xs`} data-testid={`invoice-status-${inv.session_id}`}>
                        {sty.label}
                      </Badge>
                      {inv.coupon_code && (
                        <Badge className="bg-emerald-50 text-emerald-700 text-[10px] border border-emerald-200 font-mono">
                          {inv.coupon_code} · −${inv.coupon_discount.toFixed(2)}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Mail size={12} /> {inv.customer_email}</span>
                      {created && <span>Issued {created.toLocaleDateString()}</span>}
                      {paid && isPaid && <span className="text-emerald-700">Paid {paid.toLocaleDateString()}</span>}
                      {inv.description && (
                        <span className="italic text-gray-400" data-testid={`invoice-description-${inv.session_id}`}>
                          {inv.description}
                        </span>
                      )}
                    </div>
                    {(inv.line_items || []).length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid={`invoice-line-items-${inv.session_id}`}>
                        {inv.base_amount != null && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                            Base ${Number(inv.base_amount).toFixed(2)}
                          </span>
                        )}
                        {inv.line_items.map((li, i) => (
                          <span key={i} className="text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-100">
                            {li.label} · ${Number(li.amount).toFixed(2)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      {inv.coupon_discount > 0 && (
                        <p className="text-[11px] text-gray-400 line-through">
                          ${inv.subtotal.toFixed(2)}
                        </p>
                      )}
                      <p
                        className="text-lg font-bold text-gray-900"
                        data-testid={`invoice-amount-${inv.session_id}`}
                      >
                        ${inv.amount.toFixed(2)}
                      </p>
                    </div>
                    {inv.checkout_url && (
                      <a
                        href={inv.checkout_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:text-violet-900 border border-violet-200 rounded-md px-2 py-1"
                        data-testid={`invoice-stripe-link-${inv.session_id}`}
                        title="Open the Stripe Checkout link"
                      >
                        <ExternalLink size={12} /> Stripe
                      </a>
                    )}
                    {!isPaid && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResend(inv.session_id, inv.customer_email)}
                        disabled={resendingId === inv.session_id}
                        className="text-violet-700 border-violet-200 hover:bg-violet-50"
                        data-testid={`invoice-resend-${inv.session_id}`}
                      >
                        {resendingId === inv.session_id
                          ? <Loader2 size={12} className="animate-spin" />
                          : <><Mail size={12} className="mr-1" /> Resend</>}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-16 border-0 shadow-sm text-center">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg font-medium">No invoices yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Invoices appear here automatically when you click &ldquo;Send Invoice&rdquo; on an approved job request.
          </p>
        </Card>
      )}
    </div>
  );
};

export default AdminInvoices;
